import jsPDF from "jspdf";
import { computeMedTotal } from "@/lib/medUtils";

const TEMPLATE = "/prescription_template.png";

function fmtDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

// Exact mm coordinates measured from the letterhead. y = text baseline.
const FIELDS = [
  { key: "patient_name", x: 39, y: 73, size: 12, align: "left" },
  { key: "date", x: 173, y: 73, size: 12, align: "left", date: true },
  { key: "age_gender", x: 38, y: 82.3, size: 12, align: "left" },
  { key: "drug_allergy", x: 103, y: 82.3, size: 12, align: "left" },
];

const COLS = {
  s_no: { x: 12.3, align: "center" },
  name: { x: 20, align: "left" },
  food: { x: 99.45, align: "center" },
  morning: { x: 120.95, align: "center" },
  afternoon: { x: 138.9, align: "center" },
  night: { x: 156.1, align: "center" },
  duration: { x: 172.25, align: "center" },
  total_qty: { x: 192.35, align: "center" },
};
const MMPT = 0.352778; // mm per pt
const ROW_CENTER0 = 118.35; // mm — first medicine-row band centre (from template)
const ROW_STEP = 12.95; // mm between row centres
const NAME_WRAP_W = 64; // mm — wrap width for trade name / salt
const NAME_PT = 9;
const SALT_MAX_PT = 6.5;
const SALT_MIN_PT = 4.5;
// signature box (mm) — matches alignment.js SIGNATURE
const SIG = { x: 10, y: 226, w: 52, h: 18 };

async function loadTemplateDataUrl() {
  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = TEMPLATE;
  });
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);
  return canvas.toDataURL("image/jpeg", 0.82);
}

export async function buildPdfBlob(data) {
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

  try {
    const bg = await loadTemplateDataUrl();
    pdf.addImage(bg, "JPEG", 0, 0, 210, 297);
  } catch (e) {
    // if template can't load, still produce the text-only prescription
  }

  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(15, 23, 42);

  // top fields
  FIELDS.forEach((fl) => {
    const val = fl.date ? fmtDate(data.date) : data[fl.key] || "";
    if (!val) return;
    pdf.setFontSize(fl.size);
    pdf.text(String(val), fl.x, fl.y, { align: fl.align });
  });

  // medicine rows — vertically centred; name/salt wrap, salt shrinks to fit
  (data.medicines || []).forEach((m, i) => {
    if (!m.name || !m.name.trim()) return;
    const center = ROW_CENTER0 + i * ROW_STEP;
    const midBaseline = center + NAME_PT * MMPT * 0.35; // centre a single line vertically

    pdf.setTextColor(15, 23, 42);
    pdf.setFontSize(9);
    pdf.text(String(i + 1), COLS.s_no.x, midBaseline, { align: "center" });

    // trade name + salt block
    const nameStep = NAME_PT * MMPT * 1.05;
    pdf.setFontSize(NAME_PT);
    const nameLines = pdf.splitTextToSize(String(m.name), NAME_WRAP_W);
    let saltPt = SALT_MAX_PT;
    let saltLines = [];
    if (m.salt) {
      for (;;) {
        pdf.setFontSize(saltPt);
        saltLines = pdf.splitTextToSize(String(m.salt), NAME_WRAP_W);
        const bH = nameLines.length * nameStep + saltLines.length * (saltPt * MMPT * 1.05);
        if (bH <= ROW_STEP - 1.5 || saltPt <= SALT_MIN_PT) break;
        saltPt -= 0.5;
      }
    }
    const saltStep = saltPt * MMPT * 1.05;
    const blockH = nameLines.length * nameStep + saltLines.length * saltStep;
    const blockTop = center - blockH / 2;

    pdf.setFontSize(NAME_PT);
    pdf.setFont(undefined, "bold");
    pdf.setTextColor(15, 23, 42);
    let ny = blockTop + NAME_PT * MMPT * 0.72;
    nameLines.forEach((ln) => { pdf.text(ln, COLS.name.x, ny, { align: "left" }); ny += nameStep; });
    pdf.setFont(undefined, "normal");

    if (saltLines.length) {
      pdf.setFontSize(saltPt);
      pdf.setTextColor(90, 90, 90);
      let sy = blockTop + nameLines.length * nameStep + saltPt * MMPT * 0.72;
      saltLines.forEach((ln) => { pdf.text(ln, COLS.name.x, sy, { align: "left" }); sy += saltStep; });
      pdf.setTextColor(15, 23, 42);
    }

    pdf.setFontSize(9);
    ["food", "morning", "afternoon", "night", "duration"].forEach((c) => {
      if (m[c]) pdf.text(String(m[c]), COLS[c].x, midBaseline, { align: COLS[c].align });
    });
    const total = computeMedTotal(m);
    if (total) pdf.text(total, COLS.total_qty.x, midBaseline, { align: COLS.total_qty.align });
  });

  // notes — up to 4, stacked, centred in the gap between signature and QR code
  const notesArr = String(data.notes || "").split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 4);
  if (notesArr.length) {
    pdf.setFontSize(10.5);
    pdf.setTextColor(15, 23, 42);
    const NX = 66.5;
    const NW = 90;
    const MAXY = 242;
    const IND = 3; // bullet indent (mm)
    const lineH = 10.5 * MMPT * 1.2;
    let y = 208 + 3;
    for (const note of notesArr) {
      const wrapped = pdf.splitTextToSize(note, NW - IND);
      let stop = false;
      let first = true;
      for (const wl of wrapped) {
        if (y > MAXY) { stop = true; break; }
        if (first) { pdf.text("•", NX, y); first = false; }
        pdf.text(wl, NX + IND, y);
        y += lineH;
      }
      if (stop) break;
    }
  }

  // doctor signature (transparent PNG), fit inside the box, bottom-left aligned
  if (data.doctor_signature) {
    try {
      const props = pdf.getImageProperties(data.doctor_signature);
      const ratio = props.height / props.width;
      let w = SIG.w;
      let h = w * ratio;
      if (h > SIG.h) { h = SIG.h; w = h / ratio; }
      const y = SIG.y + (SIG.h - h);
      pdf.addImage(data.doctor_signature, "PNG", SIG.x, y, w, h);
    } catch (e) { /* skip if image fails */ }
  }

  return pdf.output("blob");
}

export async function downloadPdf(data, filename = "prescription.pdf") {
  const blob = await buildPdfBlob(data);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function openWa(phone, blob, fileName) {
  const digits = (phone || "").replace(/[^0-9]/g, "");
  const base = digits ? `https://wa.me/${digits}` : "https://wa.me/";
  window.open(base, "_blank");
  if (blob) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}

export async function shareWhatsApp(data, phone = "") {
  const patient = (data.patient_name || "").trim() || "Patient";
  const fileName = `${patient} Prescription.pdf`;
  try {
    const blob = await buildPdfBlob(data);
    const file = new File([blob], fileName, { type: "application/pdf" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: "Prescription" });
      return { method: "share" };
    }
    openWa(phone, blob, fileName);
    return { method: "fallback" };
  } catch (e) {
    openWa(phone, null, fileName);
    return { method: "fallback" };
  }
}
